import React, { useEffect, useMemo, useState } from "react";
import { ArrowUp, ArrowDown, Settings, RefreshCw, Filter, Layers } from "lucide-react";
import { useMarket } from "../../features/markets/marketContext.jsx";
import { ASSET_CLASSES } from "../../features/markets/marketCatalog.js";
import { useMockMarketData } from "../../features/markets/useMockMarketData.js";

// ---------- Helpers ----------
function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function getMarketFormat(symbol, assetClass) {
  // decimals + tick size + typical sizes vary by class/symbol
  if (assetClass === ASSET_CLASSES.FX) {
    if (symbol === "USDJPY") return { decimals: 3, tick: 0.005, sizeDecimals: 2, sizeUnit: "Lots" };
    return { decimals: 5, tick: 0.00005, sizeDecimals: 2, sizeUnit: "Lots" };
  }

  if (assetClass === ASSET_CLASSES.METALS) {
    if (symbol === "XAUUSD") return { decimals: 2, tick: 0.1, sizeDecimals: 2, sizeUnit: "oz" };
    if (symbol === "XAGUSD") return { decimals: 2, tick: 0.01, sizeDecimals: 0, sizeUnit: "oz" };
    return { decimals: 2, tick: 0.01, sizeDecimals: 2, sizeUnit: "units" };
  }

  // CRYPTO defaults
  if (symbol === "BTCUSDT") return { decimals: 2, tick: 0.5, sizeDecimals: 4, sizeUnit: "BTC" };
  if (symbol === "ETHUSDT") return { decimals: 2, tick: 0.1, sizeDecimals: 3, sizeUnit: "ETH" };
  if (symbol === "SOLUSDT") return { decimals: 3, tick: 0.005, sizeDecimals: 1, sizeUnit: "SOL" };
  if (symbol === "BNBUSDT") return { decimals: 3, tick: 0.01, sizeDecimals: 2, sizeUnit: "BNB" };

  return { decimals: 2, tick: 0.01, sizeDecimals: 2, sizeUnit: "units" };
}

function formatNumber(n, decimals) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return x.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// PRNG: stable seeded randomness
function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function seedFromSymbol(symbol) {
  let h = 0;
  for (let i = 0; i < symbol.length; i++) h = (h * 31 + symbol.charCodeAt(i)) >>> 0;
  return h || 1;
}

function buildOrderBook({ mid, tick, depthLevels, seed, assetClass }) {
  // spread in ticks depends on market type
  const spreadTicks =
    assetClass === ASSET_CLASSES.FX ? 2 :
    assetClass === ASSET_CLASSES.METALS ? 3 :
    4;

  const halfSpread = (spreadTicks * tick) / 2;
  const bestBid = mid - halfSpread;
  const bestAsk = mid + halfSpread;

  const rand = mulberry32(seed);

  // base size scales by asset class
  const baseSize =
    assetClass === ASSET_CLASSES.FX ? 1.5 :       // lots
    assetClass === ASSET_CLASSES.METALS ? 25 :    // oz
    0.9;                                         // crypto units

  const sizeJitter =
    assetClass === ASSET_CLASSES.FX ? 0.9 :
    assetClass === ASSET_CLASSES.METALS ? 0.6 :
    1.2;

  const asks = [];
  let cumAsk = 0;
  for (let i = 0; i < depthLevels; i++) {
    const px = bestAsk + i * tick;
    const size = Math.max(0.0001, baseSize * (0.7 + rand() * sizeJitter) * (1 - i * 0.02));
    cumAsk += size;
    asks.push({ price: px, size, total: px * size, cumulative: cumAsk });
  }

  const bids = [];
  let cumBid = 0;
  for (let i = 0; i < depthLevels; i++) {
    const px = bestBid - i * tick;
    const size = Math.max(0.0001, baseSize * (0.7 + rand() * sizeJitter) * (1 - i * 0.02));
    cumBid += size;
    bids.push({ price: px, size, total: px * size, cumulative: cumBid });
  }

  return { bids, asks };
}

function symbolToLabel(symbol) {
  // BTCUSDT -> BTC/USDT, USDJPY -> USD/JPY, XAUUSD -> XAU/USD
  if (!symbol) return "—";
  if (symbol.includes("/")) return symbol;
  if (symbol.length === 6) return `${symbol.slice(0, 3)}/${symbol.slice(3)}`;
  if (symbol.length === 7) return `${symbol.slice(0, 3)}/${symbol.slice(3)}`; // e.g. USDJPY (6), not used
  if (symbol.length === 8) return `${symbol.slice(0, 3)}/${symbol.slice(3)}`; // fallback
  // common crypto quotes
  const commonQuotes = ["USDT", "USD", "BUSD", "USDC", "EUR"];
  const q = commonQuotes.find((qq) => symbol.endsWith(qq));
  if (q) return `${symbol.slice(0, -q.length)}/${q}`;
  return symbol;
}

// ---------- Component ----------
const OrderBook = () => {
  const { assetClass, symbol, currentMarket } = useMarket();

  const [depth, setDepth] = useState("0.01");
  const [grouping, setGrouping] = useState(10);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [totalSize, setTotalSize] = useState({ bids: 0, asks: 0 });

  // Mid price from your mock feed
  const md = useMockMarketData(symbol, "1H");
  const mid = Number(md?.last) || 0;

  const fmt = useMemo(() => getMarketFormat(symbol, assetClass), [symbol, assetClass]);

  const depthOptions = useMemo(() => {
    if (assetClass === ASSET_CLASSES.FX) {
      return [
        { value: "0.0005", label: "Tight" },
        { value: "0.001", label: "Med" },
        { value: "0.002", label: "Wide" },
      ];
    }
    if (assetClass === ASSET_CLASSES.METALS) {
      return [
        { value: "0.10", label: "0.10" },
        { value: "0.05", label: "0.05" },
        { value: "0.01", label: "0.01" },
      ];
    }
    return [
      { value: "0.1", label: "0.1" },
      { value: "0.01", label: "0.01" },
      { value: "0.001", label: "0.001" },
      { value: "0.0001", label: "0.0001" },
    ];
  }, [assetClass]);

  const groupingOptions = useMemo(() => [1, 5, 10, 25, 50, 100], []);

  // effective tick is driven by format tick but nudged by UI depth
  const effectiveTick = useMemo(() => {
    const d = Number(depth);
    if (!Number.isFinite(d) || d <= 0) return fmt.tick;
    return clamp(d, fmt.tick / 10, fmt.tick * 20);
  }, [depth, fmt.tick]);

  const { bids, asks } = useMemo(() => {
    const depthLevels = 10;
    const safeMid = mid > 0 ? mid : 1;
    const seed = seedFromSymbol(symbol) + Math.floor(safeMid * 10);

    return buildOrderBook({
      mid: safeMid,
      tick: effectiveTick,
      depthLevels,
      seed,
      assetClass,
    });
  }, [symbol, mid, effectiveTick, assetClass]);

  useEffect(() => {
    const bidsTotal = bids.reduce((sum, o) => sum + o.size, 0);
    const asksTotal = asks.reduce((sum, o) => sum + o.size, 0);
    setTotalSize({ bids: bidsTotal, asks: asksTotal });
  }, [bids, asks]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 700);
  };

  const maxSize = useMemo(() => {
    const b = bids.map((x) => x.size);
    const a = asks.map((x) => x.size);
    return Math.max(...b, ...a, 1e-9);
  }, [bids, asks]);

  const getDepthPercentage = (size) => (size / maxSize) * 100;

  const highestBid = bids[0]?.price ?? 0;
  const lowestAsk = asks[0]?.price ?? 0;
  const spread = lowestAsk - highestBid;
  const spreadPct = highestBid > 0 ? (spread / highestBid) * 100 : 0;
  const midPrice = (highestBid + lowestAsk) / 2;

  const marketLabel = currentMarket?.label ?? symbolToLabel(symbol);

  const quote = currentMarket?.quote
    ? currentMarket.quote
    : marketLabel.includes("/") ? marketLabel.split("/")[1] : "USD";

  const priceLabel = `Price (${quote})`;
  const sizeLabel = `Size (${fmt.sizeUnit})`;
  const totalLabel = assetClass === ASSET_CLASSES.FX ? "Notional" : `Total (${quote})`;

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-xl border border-gray-800 shadow-2xl h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-r from-primary-600 to-cyan-500 flex items-center justify-center">
              <Layers size={18} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white">Order Book</h3>
              <div className="text-xs text-gray-400 flex items-center gap-2">
                <span>{marketLabel}</span>
                <div className="h-1 w-1 rounded-full bg-gray-600"></div>
                <span>{assetClass === ASSET_CLASSES.CRYPTO ? "Perpetual" : "Spot"}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
              title="Refresh (visual)"
            >
              <RefreshCw size={16} className={isRefreshing ? "animate-spin" : ""} />
            </button>
            <button
              type="button"
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
              title="Settings"
            >
              <Settings size={16} />
            </button>
            <button
              type="button"
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
              title="Filter"
            >
              <Filter size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="px-4 py-3 border-b border-gray-800 bg-gray-900/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <div className="text-xs text-gray-400 mb-1">Depth</div>
              <div className="flex gap-1">
                {depthOptions.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => setDepth(o.value)}
                    className={`px-3 py-1 text-xs rounded ${
                      depth === o.value
                        ? "bg-primary-600 text-white"
                        : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-400 mb-1">Grouping</div>
              <div className="flex gap-1">
                {groupingOptions.map((o) => (
                  <button
                    key={o}
                    onClick={() => setGrouping(o)}
                    className={`px-3 py-1 text-xs rounded ${
                      grouping === o
                        ? "bg-primary-600 text-white"
                        : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white"
                    }`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="text-right">
            <div className="text-xs text-gray-400">Total Size</div>
            <div className="text-sm font-semibold text-white">
              {formatNumber(totalSize.bids, 2)} / {formatNumber(totalSize.asks, 2)} {fmt.sizeUnit}
            </div>
          </div>
        </div>
      </div>

      {/* Headers */}
      <div className="grid grid-cols-3 px-4 py-2 border-b border-gray-800 text-xs text-gray-400 bg-gray-900/30">
        <div className="text-left">{priceLabel}</div>
        <div className="text-right">{sizeLabel}</div>
        <div className="text-right">{totalLabel}</div>
      </div>

      {/* Book */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* ASKS */}
        <div className="flex-1 overflow-y-auto">
          {asks.map((ask, idx) => {
            const depthPercent = getDepthPercentage(ask.size);
            return (
              <div
                key={`ask-${idx}`}
                className="orderbook-row group relative"
                onClick={() => console.log("Clicked ask:", ask)}
              >
                <div
                  className="absolute right-0 top-0 bottom-0 bg-red-500/10"
                  style={{ width: `${depthPercent}%` }}
                />
                <div className="relative grid grid-cols-3 px-4 py-2 text-sm hover:bg-gray-800/50">
                  <div className="text-red-400 font-medium">{formatNumber(ask.price, fmt.decimals)}</div>
                  <div className="text-right text-white">{formatNumber(ask.size, fmt.sizeDecimals)}</div>
                  <div className="text-right text-gray-300">{formatNumber(ask.total, fmt.decimals)}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mid + spread */}
        <div className="px-4 py-3 border-y border-gray-800 bg-gray-900/70">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <div className="text-xs text-gray-400">Market Price</div>
                <div className="text-xl font-bold text-white">{formatNumber(highestBid, fmt.decimals)}</div>
              </div>

              <div className="h-8 w-px bg-gray-700" />

              <div>
                <div className="text-xs text-gray-400">Spread</div>
                <div className="text-sm font-semibold text-yellow-400">
                  {formatNumber(spread, fmt.decimals)} ({spreadPct.toFixed(3)}%)
                </div>
              </div>
            </div>

            <div className="text-right">
              <div className="text-xs text-gray-400">Mid Price</div>
              <div className="text-lg font-semibold text-white">{formatNumber(midPrice, fmt.decimals)}</div>
            </div>
          </div>

          <div className="flex gap-2 mt-3">
            <button
              type="button"
              className="flex-1 py-2 bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-700 hover:to-rose-800 rounded-lg text-sm font-bold text-white transition-all duration-300 flex items-center justify-center gap-2"
            >
              <ArrowDown size={16} />
              Sell {formatNumber(lowestAsk, fmt.decimals)}
            </button>
            <button
              type="button"
              className="flex-1 py-2 bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 rounded-lg text-sm font-bold text-white transition-all duration-300 flex items-center justify-center gap-2"
            >
              <ArrowUp size={16} />
              Buy {formatNumber(highestBid, fmt.decimals)}
            </button>
          </div>
        </div>

        {/* BIDS */}
        <div className="flex-1 overflow-y-auto">
          {bids.map((bid, idx) => {
            const depthPercent = getDepthPercentage(bid.size);
            return (
              <div
                key={`bid-${idx}`}
                className="orderbook-row group relative"
                onClick={() => console.log("Clicked bid:", bid)}
              >
                <div
                  className="absolute left-0 top-0 bottom-0 bg-green-500/10"
                  style={{ width: `${depthPercent}%` }}
                />
                <div className="relative grid grid-cols-3 px-4 py-2 text-sm hover:bg-gray-800/50">
                  <div className="text-green-400 font-medium">{formatNumber(bid.price, fmt.decimals)}</div>
                  <div className="text-right text-white">{formatNumber(bid.size, fmt.sizeDecimals)}</div>
                  <div className="text-right text-gray-300">{formatNumber(bid.total, fmt.decimals)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-800 bg-gray-900/50">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-400 mb-1">Bid/Ask Ratio</div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500" style={{ width: "65%" }} />
              </div>
              <span className="text-green-400 font-bold">65%</span>
            </div>
          </div>

          <div>
            <div className="text-gray-400 mb-1">Imbalance</div>
            <div className="flex items-center justify-between">
              <span className="text-white font-medium">1.28x</span>
              <span className="text-green-400 text-xs">Bullish</span>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-800">
          <div className="text-xs text-gray-400 text-center">
            Simulated order book (no backend market endpoints yet) • Symbol: {symbol}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderBook;
